import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface TablePercentageDisplayProps {
  value: string | null;
  isEmpty?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
  tooltipContent?: React.ReactNode;
  children?: React.ReactNode;
  /** Whether the cell is editable (affects cursor) */
  isEditable?: boolean;
  /** Whether this is a parent row (affects cursor/opacity) */
  isParent?: boolean;
  /** Click handler to start editing */
  onClick?: () => void;
  /** Whether this field was recently modified (for highlighting) */
  isRecentlyModified?: boolean;
}

/**
 * Percentage display component that shows the % symbol in the same position as TablePercentageInput
 */
export const TablePercentageDisplay: React.FC<TablePercentageDisplayProps> = ({
  value,
  isEmpty = false,
  hasError = false,
  errorMessage,
  className,
  tooltipContent,
  children,
  isEditable = true,
  isParent = false,
  onClick,
  isRecentlyModified = false,
}) => {
  const { t } = useTranslation();

  // Extract the numeric part (remove % symbol and anything after it in parentheses)
  const numericValue =
    value
      ?.replace(/%\s*\(.*?\)/g, '')
      .replace(/%/g, '')
      .trim()
      .replace(/\./g, ',') || null;
  // Extract the part after % (like "(2.5pt)")
  const suffixMatch = value?.match(/%\s*(\(.*?\))/);
  const suffix = suffixMatch ? suffixMatch[1] : null;

  const interactiveClasses = isParent
    ? 'cursor-pointer opacity-50'
    : isEditable
      ? 'cursor-pointer'
      : 'cursor-not-allowed opacity-50';

  const displayClasses = cn(
    'relative flex h-full w-full items-center rounded-none bg-transparent pr-6 text-right text-sm',
    hasError
      ? 'bg-red-50 border-l-2 border-red-500'
      : isRecentlyModified
        ? 'bg-blue-50 border-l-2 border-blue-500'
        : 'bg-transparent',
    interactiveClasses,
    className
  );

  const content = (
    <div className={displayClasses} onClick={onClick}>
      <span className="relative w-full text-right">
        {isEmpty ? (
          <span className="text-muted-foreground">
            {errorMessage ? t(errorMessage) : '—'}
          </span>
        ) : (
          <>
            {numericValue || '—'}
            {suffix && (
              <span className="ml-1 text-xs text-gray-500">{suffix}</span>
            )}
            {children}
          </>
        )}
      </span>
      {!isEmpty && numericValue && (
        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 select-none text-base text-gray-500">
          %
        </span>
      )}
    </div>
  );

  if (tooltipContent || (errorMessage && !isEmpty)) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        {(errorMessage && !isEmpty) || tooltipContent ? (
          <TooltipContent>
            {errorMessage && !isEmpty ? (
              <p>{t(errorMessage)}</p>
            ) : (
              tooltipContent
            )}
          </TooltipContent>
        ) : null}
      </Tooltip>
    );
  }

  return content;
};
